interface Message {
  type: "sent" | "received";
  text: string;
  time: string;
  isVoice?: boolean;
}

interface PhoneMockupProps {
  messages: Message[];
  className?: string;
}

const VoiceMessage = () => {
  const waveHeights = [4, 6, 10, 8, 14, 12, 18, 10, 6, 8, 12, 16, 10, 6, 14, 8, 10, 6, 4, 6];

  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
        <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
        </svg>
      </div>
      <div className="flex items-center gap-1 flex-1">
        <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
        <div className="flex-1 h-5 md:h-6 flex items-center gap-0.5">
          {waveHeights.map((height, i) => (
            <div key={i} className="w-0.5 md:w-1 bg-[#25D366] rounded-full" style={{ height: `${height}px` }} />
          ))}
        </div>
        <span className="text-[9px] md:text-[10px] text-[#667781] ml-1">0:03</span>
      </div>
    </div>
  );
};

const PhoneMockup = ({ messages, className = "" }: PhoneMockupProps) => {
  return (
    <div className={`relative ${className}`}>
      <div className="relative w-[240px] sm:w-[280px] md:w-[320px] h-[500px] sm:h-[580px] md:h-[640px] bg-whatsapp-dark rounded-[2.5rem] md:rounded-[3rem] p-1.5 md:p-2 shadow-phone">
        <div className="relative w-full h-full bg-background rounded-[2.5rem] overflow-hidden">
          <div className="h-12 bg-whatsapp-teal-dark flex items-center justify-center">
            <div className="w-20 h-5 bg-foreground/20 rounded-full absolute top-3" />
          </div>

          <div className="bg-whatsapp-teal-dark px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/anydo-logo.webp" alt="Any.do" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">Any.do</p>
              <p className="text-white/70 text-xs">Online</p>
            </div>
          </div>

          <div
            className="flex-1 p-3 space-y-2 h-[calc(100%-8rem)] overflow-y-auto"
            style={{ backgroundColor: "#ECE5DD" }}
          >
            <div className="relative space-y-3">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.type === "sent" ? "justify-end" : "justify-start"} animate-fade-in-up`}
                  style={{ animationDelay: `${i * 0.8 + 0.5}s` }}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-lg text-sm shadow-sm ${
                      msg.type === "sent" ? "rounded-br-sm" : "bg-white rounded-bl-sm"
                    }`}
                    style={{
                      backgroundColor: msg.type === "sent" ? "#DCF8C6" : "#FFFFFF",
                      color: "#111B21",
                      minWidth: msg.isVoice ? "180px" : undefined,
                    }}
                  >
                    {msg.isVoice ? (
                      <VoiceMessage />
                    ) : (
                      <>
                        <p className="whitespace-pre-line">
                          {msg.text.split(/(\*\*.*?\*\*|🔔)/g).map((part, index) => {
                            if (part.startsWith("**") && part.endsWith("**")) {
                              return <strong key={index}>{part.slice(2, -2)}</strong>;
                            }
                            if (part === "🔔") {
                              return (
                                <span key={index} className="notification-pulse">
                                  🔔
                                </span>
                              );
                            }
                            return part;
                          })}
                        </p>
                        <p className="text-[10px] text-right mt-1" style={{ color: "#667781" }}>
                          {msg.time}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-2" style={{ backgroundColor: "#F0F2F5" }}>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white rounded-full px-4 py-2 text-xs" style={{ color: "#667781" }}>
                Type a message
              </div>
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#25D366" }}
              >
                <svg
                  className="w-5 h-5 text-white rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="absolute -inset-4 rounded-[4rem] blur-3xl -z-10"
        style={{ backgroundColor: "rgba(37, 211, 102, 0.15)" }}
      />
    </div>
  );
};

export default PhoneMockup;
